import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// Configs, contexts and utils
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Shared components
import { LayoutSingleColumn, NamedLink, Page } from '../../components';

// Containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Same directory
import { CONTACT_EMAIL, POPULAR, REFUNDS, SECTIONS, TOPICS } from './faqContent';

import css from './FAQPage.module.css';

const ICON_PATHS = {
  learner: (
    <>
      <path d="M2 9l10-5 10 5-10 5z" />
      <path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
    </>
  ),
  trainer: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  cancel: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h11a5 5 0 010 10h-3" />
    </>
  ),
  account: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
};

const TopicIcon = ({ name }) => (
  <span className={css.topicIcon} aria-hidden="true">
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name]}
    </svg>
  </span>
);

// Search ignores case and accents: "remboursement" finds "Remboursé"
const normalize = text =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const answerText = answer =>
  answer
    .map(block => block.p || (block.steps || []).join(' ') || '')
    .concat(answer.some(block => block.refunds) ? REFUNDS.map(r => `${r.when} : ${r.refund}`) : [])
    .filter(Boolean)
    .join(' ');

const AnswerBlock = ({ block }) => {
  if (block.steps) {
    return (
      <ol className={css.steps}>
        {block.steps.map(step => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    );
  }
  if (block.refunds) {
    return (
      <ul className={css.refunds}>
        {REFUNDS.map(r => (
          <li key={r.when} className={r.full ? css.refundFull : css.refundHalf}>
            <span>{r.when}</span>
            <strong>{r.refund}</strong>
          </li>
        ))}
      </ul>
    );
  }
  if (block.link) {
    return (
      <p>
        <NamedLink name={block.link.name} className={css.answerLink}>
          {block.link.label} →
        </NamedLink>
      </p>
    );
  }
  return <p>{block.p}</p>;
};

// French spacing: keep « ? », « : » and « » » on the line of the previous word
const noOrphanPunctuation = text => text.replace(/ ([?:!»])/g, '\u00a0$1');

// A native <details>: answers open on click even before the page's JavaScript has loaded
const Question = ({ question, isOpen, onToggle }) => (
  <details
    className={css.question}
    id={question.id}
    open={isOpen}
    onToggle={e => onToggle(e.currentTarget.open)}
  >
    <summary className={css.questionButton}>
      <h3 className={css.questionTitle}>{noOrphanPunctuation(question.q)}</h3>
      <span className={css.toggleIcon} aria-hidden="true" />
    </summary>
    <div className={css.answer}>
      {question.a.map((block, i) => (
        <AnswerBlock key={i} block={block} />
      ))}
    </div>
  </details>
);

/**
 * FAQ page (/faq): questions of learners and trainers, grouped by topic, with a search.
 * Replaces the Console page /p/faq.
 *
 * @component
 * @returns {JSX.Element}
 */
const FAQPage = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState([SECTIONS[0].questions[0].id]);

  const setOpen = (id, open) =>
    setOpenIds(ids => (open ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter(i => i !== id)));

  const openQuestion = id => {
    setQuery('');
    setOpen(id, true);
    const target = typeof document !== 'undefined' && document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const search = normalize(query.trim());
  const sections = SECTIONS.map(section => ({
    ...section,
    questions: search
      ? section.questions.filter(q => normalize(`${q.q} ${answerText(q.a)}`).includes(search))
      : section.questions,
  })).filter(section => section.questions.length > 0);

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    url: `${config.marketplaceRootURL}/faq`,
    inLanguage: 'fr-BE',
    mainEntity: SECTIONS.flatMap(section =>
      section.questions.map(q => ({
        '@type': 'Question',
        name: q.q,
        acceptedAnswer: { '@type': 'Answer', text: answerText(q.a) },
      }))
    ),
  };

  return (
    <Page
      title={intl.formatMessage(
        { id: 'FAQPage.metaTitle' },
        { marketplaceName: config.marketplaceName }
      )}
      description={intl.formatMessage({ id: 'FAQPage.metaDescription' })}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.hero}>
            <p className={css.kicker}>Centre d'aide</p>
            <h1 className={css.title}>Comment pouvons-nous vous aider ?</h1>
            <p className={css.lead}>
              Les réponses aux questions les plus fréquentes des apprenants et des formateurs. Vous
              ne trouvez pas ? Notre équipe vous répond.
            </p>
            <div className={css.search}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>
              <label htmlFor="faq-search" className={css.visuallyHidden}>
                Rechercher une question
              </label>
              <input
                id="faq-search"
                type="search"
                className={css.searchInput}
                placeholder="Annulation, paiement, avis…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
            <div className={css.popular}>
              <span>Fréquent :</span>
              {POPULAR.map(p => (
                <button
                  key={p.question}
                  type="button"
                  className={css.chip}
                  onClick={() => openQuestion(p.question)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </header>

          <nav className={css.topics} aria-label="Thèmes">
            {TOPICS.map(t => (
              <a key={t.id} href={`#${t.id}`} className={css.topic}>
                <TopicIcon name={t.icon} />
                <span>
                  <strong>{t.title}</strong>
                  <small>{t.text}</small>
                </span>
              </a>
            ))}
          </nav>

          <div className={css.layout}>
            <aside className={css.sidebar}>
              <p className={css.sidebarTitle}>Sommaire</p>
              {SECTIONS.map(s => (
                <a key={s.id} href={`#${s.id}`} className={css.sidebarLink}>
                  {s.title}
                </a>
              ))}
            </aside>

            <div>
              {sections.length === 0 ? (
                <p className={css.noResult} role="status">
                  Aucune question ne correspond à « {query} ». Écrivez-nous à{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
              ) : null}
              {sections.map(section => (
                <section key={section.id} id={section.id} className={css.section}>
                  <h2 className={css.sectionTitle}>{section.title}</h2>
                  <p className={css.sectionIntro}>{section.intro}</p>
                  <div className={css.questions}>
                    {section.questions.map(q => (
                      <Question
                        key={q.id}
                        question={q}
                        isOpen={!!search || openIds.includes(q.id)}
                        onToggle={open => !search && setOpen(q.id, open)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <section className={css.contact}>
            <div className={css.contactBox}>
              <div>
                <h2 className={css.contactTitle}>Vous n'avez pas trouvé votre réponse ?</h2>
                <p className={css.contactText}>
                  Une réclamation, une question sur une réservation ? Écrivez-nous, notre équipe
                  vous répond personnellement.
                </p>
              </div>
              <a href={`mailto:${CONTACT_EMAIL}`} className={css.contactButton}>
                {CONTACT_EMAIL}
              </a>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FAQPage;
